import { RefCallback, useCallback, useEffect, useRef } from "react"

interface IntersectionObserverOptions {
  /** Scroll container el; defaults to viewport if omitted. */
  root: Element | null
  /** Margin around root when provided. */
  rootMargin?: string
}

export function useIntersectionObserver<T extends HTMLElement>(
  onIntersect: (() => void) | undefined,
  options?: IntersectionObserverOptions
): RefCallback<T> {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const ref: RefCallback<T> = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }

      if (!node || !onIntersect) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) onIntersect()
        },
        {
          root: options?.root,
          rootMargin: options?.rootMargin,
        }
      )

      observer.observe(node)

      observerRef.current = observer
    },
    [onIntersect, options?.root, options?.rootMargin]
  )

  useEffect(
    () => () => {
      observerRef.current?.disconnect()
    },
    []
  )

  return ref
}
